/** Executes SPARQL over the standard HTTP protocol via `fetch`. */
import type { Source } from "../sources/types.ts";
import type {
  ExecuteOptions,
  QueryExecutor,
  SparqlBinding,
  SparqlResults,
} from "./types.ts";
import { HttpError, withRetry } from "../util/net.ts";

export type FetchLike = (
  input: string,
  init: {
    method: string;
    headers: Record<string, string>;
    body: string;
    signal?: AbortSignal;
  },
) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;

// Invoke `fetch` as a member of the global so `this` binds correctly in browsers.
const defaultFetch: FetchLike = (input, init) => globalThis.fetch(input, init);

interface SparqlJsonResponse {
  head?: { vars?: string[] };
  results?: { bindings?: Array<Record<string, SparqlBinding>> };
  boolean?: boolean;
}

export interface FetchExecutorOptions {
  fetchImpl?: FetchLike;
  /** Wikidata asks non-browser clients to send a descriptive User-Agent. */
  userAgent?: string;
}

export class FetchSparqlExecutor implements QueryExecutor {
  private readonly fetchImpl: FetchLike;
  private readonly userAgent: string;

  constructor(opts: FetchExecutorOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? defaultFetch;
    this.userAgent = opts.userAgent ?? "nl2sparql/0.1 (https://github.com/; kglabs)";
  }

  async run(sparql: string, source: Source, opts?: ExecuteOptions): Promise<SparqlResults> {
    if (!source.endpoint) {
      throw new Error(`Source "${source.id}" has no SPARQL endpoint to execute against.`);
    }
    const started = Date.now();
    const raw = await withRetry(async () => {
      const res = await this.fetchImpl(source.endpoint!, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/sparql-results+json",
          // Browsers ignore a manual User-Agent (forbidden header); Node honors it.
          "User-Agent": this.userAgent,
        },
        body: "query=" + encodeURIComponent(sparql),
        ...(opts?.signal ? { signal: opts.signal } : {}),
      });
      const text = await res.text();
      if (!res.ok) throw new HttpError(res.status, text.slice(0, 200));
      return text;
    });

    let parsed: SparqlJsonResponse;
    try {
      parsed = JSON.parse(raw) as SparqlJsonResponse;
    } catch {
      throw new Error(`SPARQL endpoint returned non-JSON: ${raw.slice(0, 300)}`);
    }

    const elapsedMs = Date.now() - started;
    if (typeof parsed.boolean === "boolean") {
      return { head: [], rows: [], boolean: parsed.boolean, elapsedMs };
    }
    return {
      head: parsed.head?.vars ?? [],
      rows: parsed.results?.bindings ?? [],
      elapsedMs,
    };
  }
}
